import {
    CreateStrategyRequest,
    StrategyCloudNode,
    StrategyCombination,
    StrategyCombinationConnection,
    StrategyIndicatorInstance,
    StrategyInstrumentInfo,
    StrategyNode,
    StrategyNodeTemplate,
    StrategyOptimisation,
} from '~/api-services/AIStrategyBuilderService/types';
import { ExchangeRates } from '~/api-services/ExchangeService/types';
import { CURRENT_API_VERSION } from '~/constants/strategyConstants';

import { buildNodeFromTemplate } from './seeding';
import { OptimisationState, RuleState } from './types';

// Build the engine x/y reference for a connection input, e.g. {SMA.short, sma} -> "SMA.short_sma".
export function nodeRef(ref: { indicatorInstance: string; output: string }): string {
    return `${ref.indicatorInstance}_${ref.output}`;
}

export function connectionField(conn: StrategyCombinationConnection, name: string): unknown {
    return conn.fields.find((f) => f.name === name)?.defaultValue;
}

export type BuildStrategyPayloadArgs = {
    instrument: string | null;
    period: string;
    side: 'buy' | 'sell';
    quantityMagnitude: number;
    takeProfitDistance: number;
    stopLossDistance: number;
    // Frozen into frontend_json; null when the rate lookup failed (create still proceeds).
    exchangeRates: ExchangeRates | null;
    optimizationType: string;
    rules: RuleState[];
    allRulesValid: boolean;
    instrumentInfo: StrategyInstrumentInfo | null;
    nodeTemplates: Record<string, StrategyNodeTemplate>;
    indicatorInstances: Record<string, StrategyIndicatorInstance>;
    // Instance code -> display label ("SMA.short" -> "SMA short"), for the stored indicator names.
    instanceLabel: (code: string) => string;
    optimisationStates: Record<number, OptimisationState>;
    sideOptimisations: StrategyOptimisation[];
    combinationFor: (rule: RuleState) => StrategyCombination | null;
    optimisationSupportsInstrument: (opt: StrategyOptimisation) => boolean;
};

// Flatten the wizard state into the engine's create request: every rule's indicators +
// connection (IF) nodes, each namespaced by its 1-based rule number behind an "r." prefix so
// names stay unique across rules; enabled optimisations appended under an "o.<code>." namespace;
// then the single CLOUD node carrying the trade parameters and the AND'd entry expression.
export function buildStrategyPayload({
    instrument,
    period,
    side,
    quantityMagnitude,
    takeProfitDistance,
    stopLossDistance,
    exchangeRates,
    optimizationType,
    rules,
    allRulesValid,
    instrumentInfo,
    nodeTemplates,
    indicatorInstances,
    instanceLabel,
    optimisationStates,
    sideOptimisations,
    combinationFor,
    optimisationSupportsInstrument,
}: BuildStrategyPayloadArgs): CreateStrategyRequest | null {
    if (!instrument || !allRulesValid) {
        return null;
    }
    const factor = Math.pow(10, instrumentInfo?.quantityPrecision ?? 0);
    const signedQuantity = Math.round((side === 'buy' ? quantityMagnitude : -quantityMagnitude) * factor) / factor;

    const indicatorNodes: Record<string, unknown>[] = [];
    const connectionNodes: Record<string, unknown>[] = [];
    const connNames: string[] = [];
    // Display labels of the entry-rule indicators, in rule order, for frontend_json. Optimisation
    // indicators are deliberately left out: this is the list row's subtitle, and it should read
    // like the rules step ("EMA short | EMA long"), not include the entry filters.
    const indicatorLabels: string[] = [];

    rules.forEach((rule, idx) => {
        const combo = combinationFor(rule);
        if (!combo) {
            return;
        }
        const prefix = (name: string) => `r.${idx + 1}.${name}`;
        // Emit one node per indicator of the combination, in combination order.
        for (const code of combo.indicatorInstances) {
            const node = rule.nodes[code];
            if (!node) {
                return;
            }
            indicatorNodes.push({ ...node, name: prefix(code) });
            const label = instanceLabel(code);
            if (label && !indicatorLabels.includes(label)) {
                indicatorLabels.push(label);
            }
        }
        combo.connections.forEach((conn, connIdx) => {
            const tpl = nodeTemplates[conn.type];
            const node: Record<string, unknown> = tpl ? buildNodeFromTemplate(tpl) : { type: conn.type };
            node.x = prefix(nodeRef(conn.x));
            node.y = prefix(nodeRef(conn.y));
            for (const f of conn.fields) {
                node[f.name] = f.defaultValue;
            }
            // Connection names are namespaced as r.{ruleNumber}.{ifNumber}.{name} so multiple
            // IFs within one rule (e.g. ALLIGATOR's buy1/buy2) stay unique and ordered.
            if (typeof node.name === 'string') {
                node.name = `r.${idx + 1}.${connIdx + 1}.${node.name}`;
            }
            connectionNodes.push(node);
            if (typeof node.name === 'string' && node.name.length > 0) {
                connNames.push(node.name);
            }
        });
    });

    // Enabled optimisations behave like rules: their (fixed) indicator nodes carry the user's
    // edited param values, namespaced by the optimisation's machine-safe `code` ("o.<code>.*").
    // Their IF conditions are pushed into the same connNames list, so they AND-join into the
    // single entry expression alongside the rules.
    Object.keys(optimisationStates)
        .map((key) => ({ state: optimisationStates[Number(key)], opt: sideOptimisations[Number(key)] }))
        .filter(
            (entry): entry is { state: OptimisationState; opt: StrategyOptimisation } =>
                !!entry.opt && optimisationSupportsInstrument(entry.opt)
        )
        .forEach(({ state, opt }) => {
            const prefix = (name: string) => `o.${opt.code}.${name}`;
            for (const code of opt.indicatorInstances) {
                const instance = indicatorInstances[code];
                const node = state.nodes[code] ?? (instance ? buildNodeFromTemplate(instance) : null);
                if (!node) {
                    return;
                }
                indicatorNodes.push({ ...node, name: prefix(code) });
            }
            opt.connections.forEach((conn, connIdx) => {
                const tpl = nodeTemplates[conn.type];
                const node: Record<string, unknown> = tpl ? buildNodeFromTemplate(tpl) : { type: conn.type };
                node.x = prefix(nodeRef(conn.x));
                node.y = prefix(nodeRef(conn.y));
                for (const f of conn.fields) {
                    node[f.name] = f.defaultValue;
                }
                if (typeof node.name === 'string') {
                    node.name = `o.${opt.code}.${connIdx + 1}.${node.name}`;
                }
                connectionNodes.push(node);
                if (typeof node.name === 'string' && node.name.length > 0) {
                    connNames.push(node.name);
                }
            });
        });

    const cloudNode: StrategyCloudNode = {
        name: 'Cloud',
        type: 'CLOUD',
        quantity: signedQuantity,
        ifNodes: connNames,
        // The engine AND-joins names within an expression element via `&`; all rules'
        // connections become a single AND'd entry condition.
        enterExpression: connNames.length ? [connNames.join('&')] : [],
        exitCondition: {
            type: 'take_profit_stop_loss',
            stopLoss: stopLossDistance,
            takeProfit: takeProfitDistance,
        },
        optimizationType,
    };

    return {
        instrument: { code: instrument, period },
        strategy: [...indicatorNodes, ...connectionNodes, cloudNode] as unknown as StrategyNode[],
        apiVersion: CURRENT_API_VERSION,
        // The backend keeps no columns of its own for side/TP/SL/indicators any more, and it only
        // computes `backend_json` (required funds, currency totals, projected return) when
        // `exchangeRates` is present — so this blob is what makes the list rows and the result
        // screens work. See docs/ai-strategy/08-backend-api-changes.md.
        frontendJson: {
            indicators: indicatorLabels,
            ...(exchangeRates ? { exchangeRates } : {}),
            side,
            takeProfit: takeProfitDistance,
            stopLoss: stopLossDistance,
        },
    };
}
